import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  createCompanyGstRecord,
  getCompanyGstBootstrap,
  type CompanyGstBootstrap,
  type GstOption,
} from '../../api/dynamicGstConfigApi.js';
import { listDistricts, listStates, type DistrictRow, type StateRow } from '../../api/orgApi.js';
import {
  GSTIN_PATTERN as GSTIN_RE,
  PAN_PATTERN as PAN_RE,
} from '../../../../shared/contracts/backend-enums.js';
import './gst-config.css';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

const today = () => new Date().toISOString().slice(0, 10);
const value = (form: FormData, name: string) => String(form.get(name) ?? '').trim();
const optional = (form: FormData, name: string) => value(form, name) || undefined;
const optionsOf = (rows: readonly GstOption[], group: string) => rows.filter((row) => row.optionGroupCode === group);

export function M17CompanyGstConfigPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [asOn, setAsOn] = useState(today);
  const [data, setData] = useState<CompanyGstBootstrap | null>(null);
  const [states, setStates] = useState<readonly StateRow[]>([]);
  const [section, setSection] = useState('REGISTRATIONS');
  const [error, setError] = useState<string | null>(null);
  // Apart from `error`, which every bootstrap reload clears.
  const [statesError, setStatesError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await getCompanyGstBootstrap(asOn, token);
      setData(result);
      if (!result.sections.some((item) => item.code === section)) setSection(result.sections[0]?.code ?? '');
    } catch (reason) { setError((reason as Error).message); }
  }, [asOn, section, token]);
  useEffect(() => { void load(); }, [load]);

  // Geography master feeds the city list; a failure leaves the city typed by
  // hand, and is said so the empty list is not read as "no cities".
  useEffect(() => {
    listStates(token).then((res) => setStates(res.data))
      .catch((reason) => { setStates([]); setStatesError(`States could not be loaded: ${(reason as Error).message}`); });
  }, [token]);

  async function submit(event: FormEvent<HTMLFormElement>, resource: string, makeBody: (form: FormData) => unknown) {
    event.preventDefault();
    // React clears event.currentTarget once the handler yields, so hold the form first.
    const formElement = event.currentTarget;
    setBusy(true); setError(null); setNotice(null);
    try {
      await createCompanyGstRecord(resource, makeBody(new FormData(formElement)), token);
      formElement.reset(); setNotice('Configuration saved.'); await load();
      return true;
    } catch (reason) { setError((reason as Error).message); return false; }
    finally { setBusy(false); }
  }

  return <main className="gst-workspace" aria-label="company-gst-configuration">
    <header className="gst-header"><div><p className="gst-eyebrow">Company configuration</p><h1>{t('m17.tax.title')}</h1></div>
      <label>Effective on<DateInput type="date" value={asOn} onValueChange={(nextValue) => setAsOn(nextValue)} /></label></header>
    {statesError ? <p className="gst-message gst-error" role="alert">{statesError}</p> : null}
    {error ? <p className="gst-message gst-error" role="alert">{error}</p> : null}
    {notice ? <p className="gst-message" role="status">{notice}</p> : null}
    {!data ? <p>Loading...</p> : <>
      <nav className="gst-tabs" aria-label="Company GST sections">{data.sections.map((item) => <button key={item.code} type="button"
        aria-current={section === item.code ? 'page' : undefined} onClick={() => setSection(item.code)}>{item.label}</button>)}</nav>
      {section === 'REGISTRATIONS' ? <Registrations data={data} busy={busy} submit={submit} states={states} /> : null}
      {section === 'PROFILES' ? <Profiles data={data} busy={busy} submit={submit} /> : null}
      {section === 'ITEM_CLASSIFICATIONS' ? <ItemMappings data={data} busy={busy} submit={submit} /> : null}
      {section === 'TDS_SECTIONS' ? <TdsSections data={data} busy={busy} submit={submit} /> : null}
    </>}
  </main>;
}

type Submit = (event: FormEvent<HTMLFormElement>, resource: string, makeBody: (form: FormData) => unknown) => Promise<boolean>;
interface Props { readonly data: CompanyGstBootstrap; readonly busy: boolean; readonly submit: Submit }
interface RegistrationProps extends Props { readonly states: readonly StateRow[] }
const Dates = () => <><label>Effective from<DateInput name="effectiveFromDate" type="date" required /></label><label>Effective to<DateInput name="effectiveToDate" type="date" /></label></>;
const SelectOptions = ({ rows, group }: { readonly rows: readonly GstOption[]; readonly group: string }) => <>{optionsOf(rows, group).map((row) => <option key={row.gstOptionId} value={row.optionCode}>{row.optionLabel}</option>)}</>;

function Registrations({ data, busy, submit, states }: RegistrationProps) {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [companyId, setCompanyId] = useState('');
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [legalName, setLegalName] = useState('');
  const [tradeName, setTradeName] = useState('');
  const [gstinBody, setGstinBody] = useState('');
  const [city, setCity] = useState('');
  const [districts, setDistricts] = useState<readonly DistrictRow[]>([]);
  const [districtError, setDistrictError] = useState<string | null>(null);

  const jurisdiction = data.jurisdictions.find((row) => row.jurisdictionId === jurisdictionId);
  // The DB trigger rejects any GSTIN whose first two digits differ from the
  // jurisdiction's state code, so the operator never types them.
  const statePrefix = jurisdiction?.gstStateCode ?? '';
  const gstin = statePrefix + gstinBody;
  const gstinValid = GSTIN_RE.test(gstin);
  // Characters 3-12 of a GSTIN are its holder's PAN, so the server accepts only
  // the selected company's own PAN there, and none at all until one is recorded.
  const company = data.companies.find((row) => row.id === companyId);
  const companyPan = company?.panNumber && PAN_RE.test(company.panNumber) ? company.panNumber : '';
  const gstinShape = `${statePrefix || 'NN'}${companyPan || 'PAN'} + entity number, Z and check character`;

  const stateId = useMemo(
    () => states.find((row) => row.gstStateCode === statePrefix)?.stateId ?? '',
    [states, statePrefix],
  );
  useEffect(() => {
    setCity('');
    setDistrictError(null);
    if (!stateId) { setDistricts([]); return; }
    listDistricts(token, stateId).then((res) => setDistricts(res.data))
      .catch((reason) => { setDistricts([]); setDistrictError(`Districts could not be loaded: ${(reason as Error).message}`); });
  }, [stateId, token]);

  function pickCompany(id: string) {
    setCompanyId(id);
    const picked = data.companies.find((row) => row.id === id);
    setLegalName(picked?.legalName ?? '');
    setTradeName(picked?.companyName ?? '');
  }

  // Operators habitually type the whole 15-character GSTIN; drop the state code
  // they repeat rather than rejecting the entry.
  function typeGstin(raw: string) {
    let next = raw.toUpperCase().replace(/[^0-9A-Z]/g, '');
    if (statePrefix && next.length > 13 && next.startsWith(statePrefix)) {
      next = next.slice(statePrefix.length);
    }
    setGstinBody(next.slice(0, 13));
  }

  const blocker = !companyId ? 'Select a company'
    : !companyPan ? `${company?.companyName ?? 'This company'} has no valid PAN on record. Record the company PAN on the company master first`
    : !jurisdictionId ? 'Select a jurisdiction'
    : !gstinValid ? `GSTIN must read ${gstinShape}`
    : gstinBody.slice(0, companyPan.length) !== companyPan ? `GSTIN characters 3-12 must be ${companyPan}, the PAN of ${company?.companyName}`
    : !legalName.trim() ? 'Legal name is required'
    : !city ? 'Select a city'
    : null;

  return <section className="gst-section"><h2>GST registrations</h2>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'registrations', (form) => ({
      companyId, jurisdictionId, gstin,
      legalName: legalName.trim(), tradeName: tradeName.trim() || undefined, registrationTypeCode: value(form, 'registrationTypeCode'),
      addressLine1: value(form, 'addressLine1'), addressLine2: optional(form, 'addressLine2'), locality: optional(form, 'locality'),
      city, postalCode: value(form, 'postalCode'), effectiveFromDate: value(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
    })).then((saved) => { if (saved) { setCompanyId(''); setJurisdictionId(''); setLegalName(''); setTradeName(''); setGstinBody(''); setCity(''); } })}>
      <label>Company<select name="companyId" required value={companyId} onChange={(event) => pickCompany(event.target.value)}><option value="">Select</option>{data.companies.map((row) => <option key={row.id} value={row.id}>{row.companyCode} - {row.companyName}</option>)}</select></label>
      <label>Jurisdiction<select name="jurisdictionId" required value={jurisdictionId} onChange={(event) => setJurisdictionId(event.target.value)}><option value="">Select</option>{data.jurisdictions.map((row) => <option key={row.jurisdictionId} value={row.jurisdictionId}>{row.gstStateCode} - {row.jurisdictionName}</option>)}</select></label>
      <label>GSTIN<span className="gst-gstin"><span className="gst-gstin__prefix" aria-label="gstin-state-code">{statePrefix || '--'}</span>
        <input aria-label="gstin-body" value={gstinBody} maxLength={15} required disabled={!jurisdiction}
          aria-invalid={!gstinValid}
          placeholder={jurisdiction ? `${companyPan || 'PAN'}1Z…` : 'Select a jurisdiction first'}
          onChange={(event) => typeGstin(event.target.value)} /></span>
        <small aria-label="gstin-hint" role={gstinBody && !gstinValid ? 'alert' : undefined}>
          {gstinValid ? gstin : `Pattern: ${gstinShape}`}
        </small></label>
      <label>Registration type<select name="registrationTypeCode" required><option value="">Select</option><SelectOptions rows={data.options} group="REGISTRATION_TYPE" /></select></label>
      <label>Legal name<input name="legalName" required value={legalName} onChange={(event) => setLegalName(event.target.value)} placeholder="Select a company first" /></label>
      <label>Trade name<input name="tradeName" value={tradeName} onChange={(event) => setTradeName(event.target.value)} /></label>
      <label>Address line 1<input name="addressLine1" required /></label><label>Address line 2<input name="addressLine2" /></label>
      <label>Locality<input name="locality" /></label>
      <label>City{districts.length > 0
        ? <select name="city" required value={city} onChange={(event) => setCity(event.target.value)}><option value="">Select</option>{districts.map((row) => <option key={row.districtId} value={row.districtName}>{row.districtName}</option>)}</select>
        : <input name="city" required value={city} onChange={(event) => setCity(event.target.value)} placeholder={!jurisdiction ? 'Select a jurisdiction first' : stateId && !districtError ? 'No districts in this state yet' : ''} />}</label>
      {districtError ? <p className="gst-span-all gst-message gst-error" role="alert">{districtError}</p> : null}
      <label>Postal code<input name="postalCode" inputMode="numeric" pattern="[1-9][0-9]{5}" maxLength={6} required /></label><Dates />
      {blocker ? <p className="gst-span-all gst-message gst-error" role="status" aria-label="add-blocked-reason">Add registration is disabled: {blocker}</p> : null}
      <button disabled={busy || !data.capabilities.create || !!blocker} title={blocker ?? undefined}>Add registration</button>
    </form>
    <Table headers={['Company', 'GSTIN', 'Legal name', 'Jurisdiction', 'Type', 'Effective from']} rows={data.registrations.map((row) => [
      data.companies.find((item) => item.id === row.companyId)?.companyName ?? '—', row.gstin, row.legalName,
      data.jurisdictions.find((item) => item.jurisdictionId === row.jurisdictionId)?.jurisdictionName ?? '—',
      optionsOf(data.options, 'REGISTRATION_TYPE').find((item) => item.optionCode === row.registrationTypeCode)?.optionLabel ?? row.registrationTypeCode, day(row.effectiveFromDate),
    ])} />
  </section>;
}

function Profiles({ data, busy, submit }: Props) {
  const { day } = useBusinessDay();
  return <section className="gst-section"><h2>Registration profiles</h2>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'profiles', (form) => {
      const registration = data.registrations.find((row) => row.gstRegistrationId === value(form, 'gstRegistrationId'));
      return { companyId: registration?.companyId ?? '', gstRegistrationId: value(form, 'gstRegistrationId'),
        placeOfSupplyRule: value(form, 'placeOfSupplyRule'), filingFrequencyCode: optional(form, 'filingFrequencyCode'),
        roundingModeCode: value(form, 'roundingModeCode'), roundingScale: Number(value(form, 'roundingScale')),
        reverseChargeEnabled: form.get('reverseChargeEnabled') === 'on', eInvoiceEnabled: form.get('eInvoiceEnabled') === 'on',
        eWayBillEnabled: form.get('eWayBillEnabled') === 'on', effectiveFromDate: value(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate') };
    })}>
      <label>Registration<select name="gstRegistrationId" required><option value="">Select</option>{data.registrations.map((row) => <option key={row.gstRegistrationId} value={row.gstRegistrationId}>{row.gstin} - {row.legalName}</option>)}</select></label>
      <label>Place of supply rule<select name="placeOfSupplyRule" required><option value="">Select</option><SelectOptions rows={data.options} group="PLACE_OF_SUPPLY_RULE" /></select></label>
      <label>Filing frequency<select name="filingFrequencyCode"><option value="">Not configured</option><SelectOptions rows={data.options} group="FILING_FREQUENCY" /></select></label>
      <label>Rounding mode<select name="roundingModeCode" required><option value="">Select</option><SelectOptions rows={data.options} group="ROUNDING_MODE" /></select></label>
      <label>Rounding scale<input name="roundingScale" type="number" min="0" max="4" required /></label>
      <div className="gst-toggle-group"><label className="gst-check"><input name="reverseChargeEnabled" type="checkbox" /> Reverse charge</label><label className="gst-check"><input name="eInvoiceEnabled" type="checkbox" /> E-invoice</label><label className="gst-check"><input name="eWayBillEnabled" type="checkbox" /> E-way bill</label></div>
      <Dates /><button disabled={busy || !data.capabilities.create || data.registrations.length === 0}>Add profile</button>
    </form>
    <Table headers={['GSTIN', 'Place of supply', 'Filing', 'Rounding', 'E-invoice', 'Effective from']} rows={data.profiles.map((row) => [
      data.registrations.find((item) => item.gstRegistrationId === row.gstRegistrationId)?.gstin ?? '—',
      optionLabel(data.options, 'PLACE_OF_SUPPLY_RULE', row.placeOfSupplyRule), optionLabel(data.options, 'FILING_FREQUENCY', row.filingFrequencyCode),
      `${optionLabel(data.options, 'ROUNDING_MODE', row.roundingModeCode)} / ${row.roundingScale}`, row.eInvoiceEnabled ? 'Yes' : 'No', day(row.effectiveFromDate),
    ])} />
  </section>;
}

function ItemMappings({ data, busy, submit }: Props) {
  const { day } = useBusinessDay();
  // One list names both kinds of subject; the prefix says which one was picked.
  const subjectName = (row: CompanyGstBootstrap['itemMappings'][number]) => row.brandingItemId
    ? data.items.find((item) => item.brandingItemId === row.brandingItemId)?.itemName ?? '—'
    : data.expenseHeads.find((head) => head.expenseTypeId === row.expenseTypeId)?.name ?? '—';
  return <section className="gst-section"><h2>Item classifications</h2>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'item-mappings', (form) => {
      const [kind, id] = value(form, 'subject').split(':');
      return {
        ...(kind === 'expense' ? { expenseTypeId: id } : { brandingItemId: id }),
        classificationId: value(form, 'classificationId'), taxabilityType: value(form, 'taxabilityType'),
        ...(form.get('overrideConfigured') === 'on' ? { reverseChargeOverride: form.get('reverseChargeOverride') === 'on' } : {}),
        effectiveFromDate: value(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
      };
    })}>
      <label>Item<select name="subject" required><option value="">Select</option>
        <optgroup label="Branding items">{data.items.map((row) => <option key={row.brandingItemId} value={`item:${row.brandingItemId}`}>{row.itemCode} - {row.itemName}</option>)}</optgroup>
        <optgroup label="Other expenses">{data.expenseHeads.map((row) => <option key={row.expenseTypeId} value={`expense:${row.expenseTypeId}`}>{row.code} - {row.name}</option>)}</optgroup>
      </select></label>
      <label>HSN / SAC<select name="classificationId" required><option value="">Select</option>{data.classifications.map((row) => <option key={row.classificationId} value={row.classificationId}>{row.classificationType} {row.classificationCode} - {row.description}</option>)}</select></label>
      <label>Taxability<select name="taxabilityType" required><option value="">Select</option><SelectOptions rows={data.options} group="TAXABILITY_TYPE" /></select></label>
      <div className="gst-toggle-group"><label className="gst-check"><input name="overrideConfigured" type="checkbox" /> Override reverse charge</label><label className="gst-check"><input name="reverseChargeOverride" type="checkbox" /> Apply reverse charge</label></div>
      <Dates /><button disabled={busy || !data.capabilities.create}>Add item classification</button>
    </form>
    <Table headers={['Item', 'Classification', 'Taxability', 'RCM override', 'Effective from']} rows={data.itemMappings.map((row) => [
      subjectName(row),
      data.classifications.find((item) => item.classificationId === row.classificationId)?.classificationCode ?? '—',
      optionLabel(data.options, 'TAXABILITY_TYPE', row.taxabilityType), row.reverseChargeOverride == null ? '-' : row.reverseChargeOverride ? 'Yes' : 'No', day(row.effectiveFromDate),
    ])} />
  </section>;
}

/**
 * TDS sections (owner, 30 Oct): the rates and thresholds the law sets for each
 * section, entered by the administrator - nothing is seeded. A threshold left
 * blank is one the section does not have. A vendor is put under one of these
 * on its vendor master, and its payments are deducted by the record in force
 * on the day.
 */
function TdsSections({ data, busy, submit }: Props) {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { money } = useBusinessNumber();
  const amount = (form: FormData, name: string) => (value(form, name) ? Number(value(form, name)) : undefined);
  const rate = (pct: number) => `${pct}%`;
  return <section className="gst-section" aria-label="tds-sections"><h2>{t('m17.tds.heading')}</h2>
    <p className="gst-message">{t('m17.tds.note')}</p>
    <form className="gst-form-grid" onSubmit={(event) => void submit(event, 'tds-sections', (form) => ({
      sectionCode: value(form, 'sectionCode').toUpperCase(), description: value(form, 'description'),
      rateIndividualHufPct: Number(value(form, 'rateIndividualHufPct')), rateOtherPct: Number(value(form, 'rateOtherPct')),
      rateNoPanPct: Number(value(form, 'rateNoPanPct')),
      singlePaymentThreshold: amount(form, 'singlePaymentThreshold'), annualAggregateThreshold: amount(form, 'annualAggregateThreshold'),
      effectiveFromDate: value(form, 'effectiveFromDate'), effectiveToDate: optional(form, 'effectiveToDate'),
    }))}>
      <label>{t('m17.tds.sectionCode')}<input name="sectionCode" required maxLength={10} pattern="[0-9]{3}[A-Z0-9\(\)\-]{0,7}" placeholder="194C" /></label>
      <label>{t('m17.tds.description')}<input name="description" required maxLength={200} /></label>
      <label>{t('m17.tds.rateIndividualHuf')}<input name="rateIndividualHufPct" type="number" min="0" max="100" step="0.01" required /></label>
      <label>{t('m17.tds.rateOther')}<input name="rateOtherPct" type="number" min="0" max="100" step="0.01" required /></label>
      <label>{t('m17.tds.rateNoPan')}<input name="rateNoPanPct" type="number" min="0" max="100" step="0.01" required /></label>
      <label>{t('m17.tds.singleThreshold')}<input name="singlePaymentThreshold" type="number" min="0" step="0.01" /></label>
      <label>{t('m17.tds.aggregateThreshold')}<input name="annualAggregateThreshold" type="number" min="0" step="0.01" /></label>
      <Dates /><button disabled={busy || !data.capabilities.create}>{t('m17.tds.add')}</button>
    </form>
    <Table headers={[t('m17.tds.colSection'), t('m17.tds.description'), t('m17.tds.colIndividualHuf'), t('m17.tds.colOther'),
      t('m17.tds.colNoPan'), t('m17.tds.colSingle'), t('m17.tds.colAggregate'), t('m17.tds.colEffective')]} rows={data.tdsSections.map((row) => [
      row.sectionCode, row.description, rate(row.rateIndividualHufPct), rate(row.rateOtherPct), rate(row.rateNoPanPct),
      row.singlePaymentThreshold == null ? t('m17.tds.none') : money(row.singlePaymentThreshold),
      row.annualAggregateThreshold == null ? t('m17.tds.none') : money(row.annualAggregateThreshold),
      `${day(row.effectiveFromDate)} \u2013 ${day(row.effectiveToDate)}`,
    ])} />
  </section>;
}

function optionLabel(options: readonly GstOption[], group: string, code: string | null) {
  if (!code) return '-';
  return options.find((item) => item.optionGroupCode === group && item.optionCode === code)?.optionLabel ?? code;
}
function Table({ headers, rows }: { readonly headers: readonly string[]; readonly rows: readonly (readonly ReactNode[])[] }) {
  return <div className="gst-table-wrap"><table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>
    {rows.length === 0 ? <tr><td colSpan={headers.length}>No effective records.</td></tr> : rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}
  </tbody></table></div>;
}